import { api, enviarFormulario } from '../api';
/* O mesmo limite do recebimento, e do mesmo lugar: dois números iguais
   escritos em dois arquivos são dois números que um dia divergem. */
import { LIMITE_POR_ENVIO } from '../Fotos';

/**
 * As chamadas do adensamento, num lugar só.
 *
 * ─── Por que este arquivo existe ───────────────────────────────────────
 *
 * A tela tinha um ajudante `gravar(marca, caminho, corpo)` e montava o
 * caminho a partir de um sufixo. Funcionava, e escondia duas coisas:
 *
 *  1. **Quem procurasse `/adensamento/umidades` no projeto não achava a
 *     chamada.** O caminho só existia em tempo de execução.
 *  2. **O teste `26` não a enxergava.** Ele lê os `@Post` dos
 *     controladores e procura, em `web/src`, uma chamada `api()` com o
 *     caminho literal; se não acha, o registro é um beco sem saída — a API
 *     sabe criar e a interface não tem como. Cinco rotas desta tela caíram
 *     nesse laço, e o teste estava certo: a prova de que existe caminho na
 *     tela não pode depender de eu afirmar que existe.
 *
 * Aqui cada função carrega o caminho inteiro, escrito. É mais verboso e é
 * o ponto: o caminho é o contrato entre a tela e a API, e contrato se lê.
 *
 * ─── O que todas devolvem ──────────────────────────────────────────────
 *
 * O ENSAIO INTEIRO, recalculado. Nenhuma devolve só o registro que gravou.
 * É deliberado: gravar uma umidade muda o e₀, o grau de saturação e a
 * lista de pendências, e uma tela que remontasse esse estado sozinha
 * passaria a ter uma segunda opinião sobre o ensaio — que é exatamente o
 * que a `D-29` proíbe.
 */

export interface EnsaioAdensamento {
  cabecalho: any;
  inicial: any;
  final: any;
  estagios: any[];
  umidades: any[];
  indices: any[];
  pre: any;
  equipamentos: any[];
  /** O catálogo de prensas, que a tela usa para rotular a escolhida. */
  prensas: any[];
  /** As cápsulas com a tara vigente na data do ensaio. */
  capsulas: any[];
  pendencias: string[];
  /* A mesma lista com código e justificativa (0089): `pendencias` são só
     as abertas, em texto, porque é assim que as outras telas a leem. */
  pendenciasDetalhadas: {
    codigo: string; texto: string; trava: boolean;
    justificada: boolean; justificativa: string | null;
  }[];
}

export interface LeituraGravada {
  id: string;
  tempo_s: string;
  leitura_mm: string;
  origem: string;
  descartada: boolean;
  motivo_descarte: string | null;
  /** Altura do corpo de prova naquela leitura — calculada pelo banco. */
  altura_mm: string | null;
}

export const verAdensamento = (id: string) =>
  api<EnsaioAdensamento>(`/ensaios/${id}/adensamento`);

export const gravarCabecalho = (id: string, corpo: unknown) =>
  api<EnsaioAdensamento>(`/ensaios/${id}/adensamento`,
    { metodo: 'PUT', corpo });

/**
 * A planilha de um momento, inteira.
 *
 * `PUT` e não `POST`: o que sobe é o ESTADO das três linhas, não um
 * acréscimo. As três cápsulas são pesadas em sequência na mesma balança;
 * gravar linha a linha fazia a média aparecer no meio da digitação,
 * calculada sobre uma planilha que ainda não existia.
 *
 * O `POST` que gravava uma determinação por vez foi retirado junto com o
 * botão «acrescentar» que o chamava. Duas portas para o mesmo campo é o
 * caminho mais curto para duas verdades sobre o mesmo ensaio.
 */
export const gravarUmidades = (id: string, corpo: unknown) =>
  api<EnsaioAdensamento>(`/ensaios/${id}/adensamento/umidades`,
    { metodo: 'PUT', corpo });

/** A massa final, pesada no desmonte do corpo de prova (6.5.7). */
export const gravarFechamento = (id: string, corpo: unknown) =>
  api<EnsaioAdensamento>(`/ensaios/${id}/adensamento/fechamento`,
    { metodo: 'PUT', corpo });

/** Descartar não é apagar: a linha fica, com o motivo, e sai da média. */
export const descartarUmidade = (id: string, uid: string, motivo: string) =>
  api<EnsaioAdensamento>(
    `/ensaios/${id}/adensamento/umidades/${uid}/descartar`,
    { metodo: 'POST', corpo: { motivo } });

export const gravarEstagios = (id: string, corpo: unknown) =>
  api<EnsaioAdensamento>(`/ensaios/${id}/adensamento/estagios`,
    { metodo: 'POST', corpo });

export const verLeituras = (id: string, eid: string) =>
  api<LeituraGravada[]>(
    `/ensaios/${id}/adensamento/estagios/${eid}/leituras`);

export const descartarLeitura = (id: string, lid: string, motivo: string) =>
  api<EnsaioAdensamento>(
    `/ensaios/${id}/adensamento/leituras/${lid}/descartar`,
    { metodo: 'POST', corpo: { motivo } });

export const gravarCv = (id: string, eid: string, corpo: unknown) =>
  api<EnsaioAdensamento>(`/ensaios/${id}/adensamento/estagios/${eid}/cv`,
    { metodo: 'PUT', corpo });

export const gravarIndices = (id: string, corpo: unknown) =>
  api<EnsaioAdensamento>(`/ensaios/${id}/adensamento/indices`,
    { metodo: 'PUT', corpo });

export const gravarPreAdensamento = (id: string, corpo: unknown) =>
  api<EnsaioAdensamento>(`/ensaios/${id}/adensamento/pre-adensamento`,
    { metodo: 'PUT', corpo });

// ── o bruto da prensa e as fotos do corpo de prova ─────────────────────

export interface EstagioConferido {
  etapa: number;
  leituras: number;
  tempoFinalMin: number;
  deslocamentoFinalMm: number;
  tensaoLidaMinKpa: number | null;
  tensaoLidaMaxKpa: number | null;
  /** Média PONDERADA NO TEMPO — é dela que sai a variação. */
  tensaoLidaMediaKpa: number | null;
  desvioKpa: number | null;
  desvioPct: number | null;
  /** Passou das DUAS tolerâncias (relativa e absoluta). */
  fora: boolean;
  programado: boolean;
  pressaoNominalKpa: number | null;
  fase: string | null;
  alerta: string | null;
}

export interface ConferenciaBruto {
  simulacao: boolean;
  layout: string;
  prensa: string;
  arquivo: string;
  bytes: number;
  idEnsaioNoArquivo: string | null;
  numeroTeste: string | null;
  alturaInicialMm: number | null;
  diametroMm: number | null;
  etapasDeclaradas: number | null;
  leiturasNoArquivo: number;
  estagios: EstagioConferido[];
  avisos: string[];
  semEstagio: number[];
  leiturasImportadas?: number;
}

/**
 * Manda o arquivo da prensa.
 *
 * `simular` faz a viagem inteira — lê, casa com os estágios, confere as
 * tensões — e NÃO grava. É o que a tela mostra antes de a pessoa
 * confirmar: importar 4 665 leituras do corpo de prova errado não se
 * desfaz num banco que não apaga.
 */
export function importarBruto(
  id: string,
  arquivo: File,
  prensa: string,
  simular: boolean,
  aoProgredir?: (f: number) => void,
): Promise<ConferenciaBruto> {
  const form = new FormData();
  form.append('arquivo', arquivo);
  form.append('prensa', prensa);
  if (simular) form.append('simular', 'true');
  return enviarFormulario<ConferenciaBruto>(
    `/ensaios/${id}/adensamento/importar`, form, aoProgredir);
}

export type TipoFoto = 'cp_antes' | 'cp_depois' | 'moldagem' | 'montagem'
  | 'ruptura' | 'equipamento' | 'outro';

/**
 * Sobe as fotos do corpo de prova em blocos de dez.
 *
 * ── Por que os blocos ────────────────────────────────────────────────
 *
 * O servidor aceita no máximo dez arquivos por requisição
 * (`LIMITE_ARQUIVOS_LOTE`). Esta função mandava TODOS de uma vez, e
 * selecionar doze fotos do CP devolvia `{"message":"Too many files"}` —
 * em inglês, sem dizer o limite, e sem que nenhuma foto subisse.
 *
 * É o achado 3 do documento 72 na ficha do ensaio: lá era o recebimento
 * (já resolvido em `Fotos.tsx`), aqui era a mesma armadilha, viva.
 *
 * Um bloco por vez e em série, não em paralelo: quem está fotografando o
 * corpo de prova costuma estar no laboratório com a rede da casa, e dez
 * envios simultâneos de foto de celular é como metade falha por tempo
 * esgotado.
 */
export async function enviarFotos(
  id: string,
  fotos: { arquivo: File; tipo: TipoFoto; legenda?: string }[],
  aoProgredir?: (f: number) => void,
): Promise<void> {
  const blocos: typeof fotos[] = [];
  for (let i = 0; i < fotos.length; i += LIMITE_POR_ENVIO) {
    blocos.push(fotos.slice(i, i + LIMITE_POR_ENVIO));
  }

  for (let b = 0; b < blocos.length; b++) {
    const form = new FormData();
    for (const f of blocos[b]) form.append('fotos', f.arquivo, f.arquivo.name);
    form.append('itens', JSON.stringify(
      blocos[b].map((f) => ({ tipo: f.tipo, legenda: f.legenda || undefined }))));
    await enviarFormulario(
      `/ensaios/${id}/adensamento/fotos`, form,
      (fr) => aoProgredir?.((b + fr) / blocos.length));
  }
}

export function desconsiderarFoto(id: string, fid: string, motivo: string) {
  return api(`/ensaios/${id}/adensamento/fotos/${fid}/desconsiderar`,
    { metodo: 'POST', corpo: { motivo } });
}

/** A conferência gravada: nominal × média lida, por estágio. */
export interface ConferenciaTensao {
  estagio_id: string;
  ordem: number;
  fase: string;
  nominal_kpa: string;
  media_kpa: string | null;
  minima_kpa: string | null;
  maxima_kpa: string | null;
  leituras: number | null;
  desvio_kpa: string | null;
  desvio_pct: string | null;
  tensao_tolerancia_rel: string;
  tensao_tolerancia_kpa: string;
  fora: boolean;
  conferido: boolean;
  conferido_em: string | null;
  justificativa: string | null;
  conferido_por_nome: string | null;
}

/**
 * Registra que alguém olhou o desvio e o explicou.
 *
 * Não corrige a leitura — corrigir dado bruto de equipamento é o que a
 * restrição 25 proíbe. O desvio continua onde estava; o que muda é que
 * ele deixou de ser desconhecido, e a pendência que travava o laudo sai.
 */
export function conferirTensao(id: string, estagioId: string, motivo: string) {
  return api(`/ensaios/${id}/adensamento/estagios/${estagioId}/conferir-tensao`,
    { metodo: 'POST', corpo: { motivo } });
}
